import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { F } from "../fonts";
import { useDesigns } from "../api";

const C = {
  vanilla: "#F8F4EE",
  umber:   "#443A35",
};

const UIUXPage = () => {
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState(null); // null | image src
  const { designs } = useDesigns();
  const uiuxDesigns = designs.filter((d) => d.category === "UI/UX");

  useEffect(() => {
    if (!lightbox) return;
    const onKeyDown = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightbox]);

  return (
    <Box sx={{ backgroundColor: C.umber, minHeight: "100vh", color: C.vanilla }}>

      {/* ── Top bar ── */}
      <Box sx={{
        px: "40px", py: "22px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid rgba(248,244,238,0.08)",
        position: "sticky", top: 0, zIndex: 100,
        backgroundColor: "rgba(68,58,53,0.92)",
        backdropFilter: "blur(12px)",
      }}>
        <Box
          onClick={() => navigate("/")}
          sx={{
            display: "flex", alignItems: "center", gap: 1.5,
            cursor: "pointer", color: "rgba(248,244,238,0.5)",
            transition: "color 0.2s ease",
            "&:hover": { color: C.vanilla },
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          <Typography sx={{ fontSize: "12px", letterSpacing: "2.5px", fontFamily: F.heading }}>BACK</Typography>
        </Box>
      </Box>

      {/* ── Page title ── */}
      <Box sx={{ px: "40px", pt: "70px", pb: "60px" }}>
        <Typography sx={{
          fontSize: "clamp(60px, 9vw, 110px)",
          fontFamily: F.heading,
          fontWeight: 700,
          letterSpacing: "5px",
          lineHeight: 0.95,
          color: C.vanilla,
        }}>
          UI/UX
        </Typography>
        <Typography sx={{
          fontSize: "clamp(60px, 9vw, 110px)",
          fontFamily: F.heading,
          fontWeight: 700,
          letterSpacing: "5px",
          lineHeight: 0.95,
          color: "rgba(248,244,238,0.12)",
        }}>
          DESIGN
        </Typography>
        <Typography sx={{
          mt: 4,
          fontSize: "14px",
          fontFamily: F.body,
          color: "rgba(248,244,238,0.38)",
          letterSpacing: "0.5px",
          maxWidth: "480px",
          lineHeight: 1.7,
        }}>
          A UI/UX redesign for a website — rethinking flows, hierarchy, and
          interface details to make the product feel effortless to use.
        </Typography>
      </Box>

      {/* ── Showcase ── */}
      <Box sx={{ px: "40px", pb: "100px" }}>
        {uiuxDesigns.map((design, idx) => (
          <Box key={design._id} sx={{ mb: idx < uiuxDesigns.length - 1 ? "96px" : 0 }}>
            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              mb: "24px",
              pb: "16px",
              borderBottom: "1px solid rgba(248,244,238,0.08)",
            }}>
              <Typography sx={{
                fontSize: "10px",
                fontFamily: "monospace",
                color: "rgba(248,244,238,0.25)",
                letterSpacing: "1px",
              }}>
                {String(idx + 1).padStart(2, "0")}
              </Typography>
              <Typography sx={{
                fontSize: "12px",
                letterSpacing: "4px",
                fontFamily: F.heading,
                fontWeight: 600,
                color: C.vanilla,
              }}>
                {design.title.toUpperCase()}
              </Typography>
              <Box sx={{ flex: 1, height: "1px", backgroundColor: "rgba(248,244,238,0.06)" }} />
            </Box>

            {design.coverImage && (
              <Box
                onClick={() => setLightbox(design.coverImage)}
                sx={{
                  borderRadius: "8px",
                  overflow: "hidden",
                  cursor: "pointer",
                  position: "relative",
                  backgroundColor: "rgba(248,244,238,0.08)",
                  transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
                  "&:hover": { transform: "scale(1.01)" },
                  "&:hover .po": { opacity: 1 },
                }}
              >
                <Box component="img" src={design.coverImage} alt="" sx={{ width: "100%", display: "block" }} />
                <Box className="po" sx={{
                  position: "absolute", inset: 0,
                  backgroundColor: "rgba(255,255,255,0.05)",
                  opacity: 0,
                  transition: "opacity 0.3s ease",
                }} />
              </Box>
            )}

            {/* ── Screens grid ── */}
            {design.gallery?.length > 0 && (
              <Box sx={{ mt: "40px" }}>
                <Box sx={{
                  display: "flex", alignItems: "center", gap: "20px",
                  mb: "24px", pb: "16px",
                  borderBottom: "1px solid rgba(248,244,238,0.08)",
                }}>
                  <Typography sx={{ fontSize: "12px", letterSpacing: "4px", fontFamily: F.heading, fontWeight: 600, color: C.vanilla }}>SCREENS</Typography>
                  <Box sx={{ flex: 1, height: "1px", backgroundColor: "rgba(248,244,238,0.06)" }} />
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gridTemplateRows: "repeat(2, auto)", gridAutoFlow: "column", gap: "8px" }}>
                  {design.gallery.map((src, i) => (
                    <Box
                      key={i}
                      sx={{ borderRadius: "6px", overflow: "hidden" }}
                    >
                      <Box
                        component="img"
                        src={src}
                        alt={`screen ${i + 1}`}
                        sx={{ width: "100%", display: "block" }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* ── Description ── */}
            <Box sx={{
              mt: "56px", pt: "40px",
              borderTop: "1px solid rgba(248,244,238,0.08)",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
              gap: { xs: "32px", md: "80px" },
            }}>
              {/* Meta */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                {[
                  { label: "PROJECT", value: design.title },
                  { label: "TOOLS",   value: design.tags?.length ? design.tags.join(", ") : "—" },
                  { label: "YEAR",    value: design.completionDate ? new Date(design.completionDate).getFullYear() : "—" },
                ].map(({ label, value }) => (
                  <Box key={label}>
                    <Typography sx={{ fontSize: "10px", letterSpacing: "3px", fontFamily: F.heading, color: "rgba(248,244,238,0.3)", mb: 0.5 }}>{label}</Typography>
                    <Typography sx={{ fontSize: "14px", fontFamily: F.body, color: C.vanilla }}>{value}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Description text */}
              <Box>
                {design.description?.split("\n\n").map((paragraph, i) => (
                  <Typography
                    key={i}
                    sx={{ fontSize: "15px", fontFamily: F.body, color: "rgba(248,244,238,0.6)", lineHeight: 1.9, mb: 2.5 }}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* ── Lightbox ── */}
      {lightbox && (
        <Box
          onClick={() => setLightbox(null)}
          sx={{
            position: "fixed", inset: 0, zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
            backgroundColor: "rgba(68,58,53,0.75)",
            backdropFilter: "blur(16px)",
            cursor: "pointer",
          }}
        >
          <Box
            onClick={() => setLightbox(null)}
            sx={{
              position: "absolute", top: "28px", right: "40px",
              width: "36px", height: "36px",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", opacity: 0.7,
              transition: "opacity 0.2s ease",
              "&:hover": { opacity: 1 },
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F8F4EE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </Box>
          <Box
            component="img"
            src={lightbox}
            alt=""
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: "85vw", maxHeight: "85vh",
              objectFit: "contain",
              borderRadius: "4px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              cursor: "default",
            }}
          />
        </Box>
      )}

    </Box>
  );
};

export default UIUXPage;
